import React, { useCallback, useEffect, useMemo } from 'react';
import type { WithIntlProps } from 'react-intl';
import { injectIntl, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Flex from '@atlaskit/primitives/components/flex';

import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';

import { sharedi18n } from '../sharedi18n.ts';
import { ContentStateColor } from '../ContentStateColor.tsx';
import { colorCodeToColorName } from '../helpers.ts';

import type { RendererContentStateComponentProps } from './types.ts';

const i18n = defineMessages({
	changeStatusAriaLabel: {
		id: 'content-state.change-status-aria-label',
		description:
			'Accessible label for content state button that includes the action and status name for screen readers',
		defaultMessage: 'Change Status: {statusName}',
	},
});

const rendererContentStateWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	cursor: 'default',
	minHeight: '32px',
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.small',
	whiteSpace: 'nowrap',
	marginLeft: 'space.0',
	marginRight: 'space.050',
	height: '24px',
});

const contentStateNameStyles = xcss({
	font: 'font.body.small',
	color: 'color.text.subtle',
});

const getBackgroundColor = () => {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		return '#00000000';
	}
	return token('utility.elevation.surface.current');
};

const RendererContentStateComponentImpl = ({
	contentId,
	contentState,
	isDraft,
	intl: { formatMessage },
}: RendererContentStateComponentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isPageOwnerRestricted = contentState?.restrictionLevel === 'PAGE_OWNER';
	const isVerifiedContentState = isPageOwnerRestricted && contentState.name === 'Verified';

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'rendererContentState',
				attributes: {
					color: colorCodeToColorName(contentState?.color?.toUpperCase()),
					restrictionLevel: contentState?.restrictionLevel?.toUpperCase(),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, contentState, contentId]);

	const handleMouseEnter = useCallback(() => {
		if (!isDraft) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'hovered',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'rendererContentStateButtonHovered',
					source: 'rendererContentStateButton',
					attributes: {
						contentId,
					},
				},
			}).fire();
		}
	}, [contentId, isDraft, createAnalyticsEvent]);

	const { isRbacEnabled } = useSpaceRoleMode();

	const contentStateBackgroundColor = getBackgroundColor();

	const verifiedRestrictedTooltipContent = isRbacEnabled
		? sharedi18n.verifiedRestrictedRbac
		: sharedi18n.verifiedRestricted;

	const tooltipContent = useMemo(
		() =>
			isPageOwnerRestricted
				? formatMessage(verifiedRestrictedTooltipContent)
				: formatMessage(sharedi18n.pageStatus),
		[isPageOwnerRestricted, formatMessage, verifiedRestrictedTooltipContent],
	);

	const contentStateColorAppearance = isVerifiedContentState
		? 'contentStateButton'
		: 'subtleRendererContentState';

	return (
		<Flex>
			<Tooltip content={tooltipContent} position="bottom">
				<Box
					as="li"
					xcss={rendererContentStateWrapperStyles}
					onMouseEnter={handleMouseEnter}
					testId="renderer-content-state-wrapper"
					style={{
						backgroundColor: contentStateBackgroundColor,
					}}
					aria-label={formatMessage(i18n.changeStatusAriaLabel, {
						statusName: contentState?.name,
					})}
				>
					<ContentStateColor
						appearance={contentStateColorAppearance}
						dataTestId="renderer-content-state-color"
						color={contentState?.color}
						restrictionLevel={contentState?.restrictionLevel}
					/>
					<Box xcss={contentStateNameStyles}>{contentState?.name}</Box>
				</Box>
			</Tooltip>
		</Flex>
	);
};

export const RendererContentStateComponent: React.FC<
	WithIntlProps<RendererContentStateComponentProps>
> & {
	WrappedComponent: React.ComponentType<RendererContentStateComponentProps>;
} = injectIntl(RendererContentStateComponentImpl);
